import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Hand,
  Loader2,
  Trash2,
  Plus,
  RefreshCw,
  EyeOff,
  ArrowLeft,
  ImageIcon,
  Video,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMoodboardPage = () => {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [available, setAvailable] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState({});
  const [tab, setTab] = useState("featured"); // 'featured' | 'pick'

  const loadAll = async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/admin/moodboard/settings`, { headers: auth }),
        axios.get(`${API}/admin/moodboard/available`, { headers: auth }),
      ]);
      setSettings(s.data);
      setAvailable(a.data);
    } catch (e) {
      toast.error("Could not load moodboard settings.");
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    loadAll();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  const setMode = async (mode) => {
    try {
      await axios.patch(`${API}/admin/moodboard/settings`, { mode }, { headers: auth });
      setSettings((s) => ({ ...s, mode }));
      toast.success(
        mode === "ai"
          ? "Switched to AI control — automatic curation is live."
          : "Switched to your manual control — only your pinned items show."
      );
    } catch (e) {
      toast.error("Failed to switch mode.");
    }
  };

  const setBusy = (key, val) => setBusyIds((m) => ({ ...m, [key]: val }));

  const feature = async (item) => {
    const key = `${item.collection}:${item.id}`;
    setBusy(key, true);
    try {
      const r = await axios.post(
        `${API}/admin/moodboard/feature`,
        { collection: item.collection, id: item.id },
        { headers: auth }
      );
      setSettings((s) => ({
        ...s,
        featured_items: [r.data.featured, ...(s.featured_items || []).filter(
          (f) => !(f.collection === item.collection && f.id === item.id)
        )],
      }));
      toast.success("Pinned to moodboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pin.");
    } finally {
      setBusy(key, false);
    }
  };

  const unfeature = async (item) => {
    const key = `${item.collection}:${item.id}`;
    setBusy(key, true);
    try {
      await axios.post(
        `${API}/admin/moodboard/unfeature`,
        { collection: item.collection, id: item.id },
        { headers: auth }
      );
      setSettings((s) => ({
        ...s,
        featured_items: (s.featured_items || []).filter(
          (f) => !(f.collection === item.collection && f.id === item.id)
        ),
      }));
      toast.success("Removed from moodboard.");
    } catch (e) {
      toast.error("Could not remove.");
    } finally {
      setBusy(key, false);
    }
  };

  const hide = async (item) => {
    const key = `${item.collection}:${item.id}`;
    setBusy(key, true);
    try {
      const r = await axios.post(
        `${API}/admin/moodboard/hide`,
        { collection: item.collection, id: item.id },
        { headers: auth }
      );
      setSettings((s) => ({
        ...s,
        hidden_items: [...(s.hidden_items || []).filter(
          (h) => !(h.collection === item.collection && h.id === item.id)
        ), r.data.hidden],
      }));
      toast.success("Hidden from AI moodboard.");
    } catch (e) {
      toast.error("Could not hide.");
    } finally {
      setBusy(key, false);
    }
  };

  const unhide = async (item) => {
    const key = `${item.collection}:${item.id}`;
    setBusy(key, true);
    try {
      await axios.post(
        `${API}/admin/moodboard/unhide`,
        { collection: item.collection, id: item.id },
        { headers: auth }
      );
      setSettings((s) => ({
        ...s,
        hidden_items: (s.hidden_items || []).filter(
          (h) => !(h.collection === item.collection && h.id === item.id)
        ),
      }));
      toast.success("Unhidden.");
    } catch (e) {
      toast.error("Could not unhide.");
    } finally {
      setBusy(key, false);
    }
  };

  const flushCache = async () => {
    try {
      await axios.post(`${API}/admin/moodboard/flush-cache`, {}, { headers: auth });
      toast.success("Public moodboard cache cleared.");
    } catch (e) {
      toast.error("Flush failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
      </div>
    );
  }

  const mode = settings?.mode || "ai";
  const featured = settings?.featured_items || [];
  const hidden = settings?.hidden_items || [];
  const isFeatured = (it) =>
    featured.some((f) => f.collection === it.collection && f.id === it.id);
  const isHidden = (it) =>
    hidden.some((h) => h.collection === it.collection && h.id === it.id);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8" data-testid="admin-moodboard-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-ink-500 hover:text-ink-950"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to admin
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-coral-500">
            Admin · Moodboard control
          </p>
          <h1 className="text-4xl font-black text-ink-950 mt-1 tracking-tight">
            Curate the Live Moodboard
          </h1>
          <p className="text-ink-500 mt-2 max-w-xl">
            Choose who decides what shows on the public landing-page moodboard:
            the AI (auto-curated) or you (manual pin list).
          </p>
        </div>
        <Button
          variant="outline"
          onClick={flushCache}
          data-testid="moodboard-flush-cache"
        >
          <RefreshCw className="w-4 h-4" />
          Flush public cache
        </Button>
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setMode("ai")}
          data-testid="moodboard-mode-ai"
          className={`text-left p-6 rounded-3xl border-2 transition-all ${
            mode === "ai"
              ? "border-coral-500 bg-gradient-to-br from-coral-500/10 to-amber-500/10 shadow-ambient"
              : "border-ink-200 bg-white hover:border-ink-950"
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-coral-500 to-amber-500 text-white flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold text-ink-950">AI Auto-Curate</p>
              <p className="text-xs text-ink-500">Last 14 days, freshest first</p>
            </div>
            {mode === "ai" && (
              <CheckCircle
                className="w-5 h-5 text-coral-500"
                data-testid="moodboard-ai-active"
              />
            )}
          </div>
          <p className="text-sm text-ink-700">
            The system shows the freshest 12 community creations automatically.
            Items you hide below stay hidden — everything else is up to the AI.
          </p>
        </button>

        <button
          type="button"
          onClick={() => setMode("manual")}
          data-testid="moodboard-mode-manual"
          className={`text-left p-6 rounded-3xl border-2 transition-all ${
            mode === "manual"
              ? "border-coral-500 bg-gradient-to-br from-coral-500/10 to-amber-500/10 shadow-ambient"
              : "border-ink-200 bg-white hover:border-ink-950"
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-ink-950 text-white flex items-center justify-center">
              <Hand className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold text-ink-950">My Manual Pick</p>
              <p className="text-xs text-ink-500">{featured.length} items pinned</p>
            </div>
            {mode === "manual" && (
              <CheckCircle
                className="w-5 h-5 text-coral-500"
                data-testid="moodboard-manual-active"
              />
            )}
          </div>
          <p className="text-sm text-ink-700">
            Only items you pin appear on the public landing page, in the order
            you add them. Total control over what visitors see first.
          </p>
        </button>
      </div>

      {/* Tab switch between current pins and the picker */}
      <div className="flex items-center gap-2 border-b border-ink-200">
        {[
          { id: "featured", label: `Pinned (${featured.length})`, icon: CheckCircle },
          { id: "pick", label: "Pick new items", icon: Plus },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`moodboard-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${
                active
                  ? "border-coral-500 text-ink-950"
                  : "border-transparent text-ink-500 hover:text-ink-950"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Featured list */}
      {tab === "featured" && (
        <div>
          {featured.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-ink-200 p-12 text-center bg-white">
              <Hand className="w-10 h-10 mx-auto text-ink-300 mb-3" />
              <p className="text-sm text-ink-500 mb-4">
                You haven&apos;t pinned anything yet. Switch to the picker to add items.
              </p>
              <Button
                onClick={() => setTab("pick")}
                className="bg-ink-950 text-white"
                data-testid="moodboard-go-pick"
              >
                <Plus className="w-4 h-4" />
                Pick items
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured.map((f) => {
                const key = `${f.collection}:${f.id}`;
                return (
                  <div
                    key={key}
                    className="relative rounded-2xl overflow-hidden border border-ink-200 bg-white group"
                    data-testid={`moodboard-featured-${f.id}`}
                  >
                    <div className="aspect-square bg-ink-100">
                      {f.kind === "video" ? (
                        <video
                          src={f.media_url}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={f.media_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                        {f.kind}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!busyIds[key]}
                        onClick={() => unfeature(f)}
                        data-testid={`moodboard-unfeature-${f.id}`}
                        className="text-rose-600"
                      >
                        {busyIds[key] ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Picker */}
      {tab === "pick" && (
        <div className="space-y-8">
          {/* Images */}
          <section>
            <h2 className="text-sm font-bold text-ink-950 mb-3 inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-coral-500" />
              Images ({available.images.length})
            </h2>
            {available.images.length === 0 ? (
              <p className="text-sm text-ink-500">No images yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {available.images.map((it) => {
                  const key = `${it.collection}:${it.id}`;
                  const featuredNow = isFeatured(it);
                  const hiddenNow = isHidden(it);
                  return (
                    <div
                      key={key}
                      className="relative rounded-2xl overflow-hidden border border-ink-200 bg-white group"
                      data-testid={`moodboard-pick-image-${it.id}`}
                    >
                      <div className="aspect-square bg-ink-100">
                        <img
                          src={it.media_url}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2 grid grid-cols-2 gap-1">
                        <Button
                          size="sm"
                          variant={featuredNow ? "default" : "outline"}
                          disabled={!!busyIds[key]}
                          onClick={() => (featuredNow ? unfeature(it) : feature(it))}
                          data-testid={`moodboard-pick-image-feature-${it.id}`}
                          className={featuredNow ? "bg-coral-500 text-white" : ""}
                        >
                          {featuredNow ? "Pinned" : "Pin"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!busyIds[key]}
                          onClick={() => (hiddenNow ? unhide(it) : hide(it))}
                          data-testid={`moodboard-pick-image-hide-${it.id}`}
                          className={hiddenNow ? "text-rose-600" : "text-ink-500"}
                          title={hiddenNow ? "Currently hidden from AI" : "Hide from AI"}
                        >
                          <EyeOff className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Videos */}
          <section>
            <h2 className="text-sm font-bold text-ink-950 mb-3 inline-flex items-center gap-2">
              <Video className="w-4 h-4 text-coral-500" />
              Videos ({available.videos.length})
            </h2>
            {available.videos.length === 0 ? (
              <p className="text-sm text-ink-500">No videos yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {available.videos.map((it) => {
                  const key = `${it.collection}:${it.id}`;
                  const featuredNow = isFeatured(it);
                  const hiddenNow = isHidden(it);
                  return (
                    <div
                      key={key}
                      className="relative rounded-2xl overflow-hidden border border-ink-200 bg-white group"
                      data-testid={`moodboard-pick-video-${it.id}`}
                    >
                      <div className="aspect-square bg-ink-100">
                        <video
                          src={it.media_url}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-2 grid grid-cols-2 gap-1">
                        <Button
                          size="sm"
                          variant={featuredNow ? "default" : "outline"}
                          disabled={!!busyIds[key]}
                          onClick={() => (featuredNow ? unfeature(it) : feature(it))}
                          data-testid={`moodboard-pick-video-feature-${it.id}`}
                          className={featuredNow ? "bg-coral-500 text-white" : ""}
                        >
                          {featuredNow ? "Pinned" : "Pin"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!busyIds[key]}
                          onClick={() => (hiddenNow ? unhide(it) : hide(it))}
                          data-testid={`moodboard-pick-video-hide-${it.id}`}
                          className={hiddenNow ? "text-rose-600" : "text-ink-500"}
                          title={hiddenNow ? "Currently hidden from AI" : "Hide from AI"}
                        >
                          <EyeOff className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminMoodboardPage;
